import { Select, SelectContent, SelectItem } from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, Lightbulb, Minus, Search, X } from "lucide-react"
import { useMemo, useState, type ReactNode } from "react"
import type { CandidateArticle } from "../../../../../api/types"
import { Highlight } from "../../highlight"
import { textTerms, type FilterToken } from "../../studio/TableSearch"
import type { Geltung } from "../mapping/geltungMapping"
import {
  articleFields,
  articleMatches,
  fieldHas,
  referenceMatches,
} from "./articleSearch"
import * as browserCounts from "./browserCounts"
import { RemapPopover } from "./RemapPopover"
import type { SearchSuggestion } from "./suggestions"

const isUnmapped = (a: CandidateArticle | undefined) =>
  a?.matchConfidence === "suggested" || a?.matchConfidence === "unresolved"

export type BrowserView =
  | { kind: "all" }
  | { kind: "suggestion"; index: number }
  | { kind: "statement"; cardId: string }

/** One statement of the Verordnung, offered as a quick view of its applied
 *  articles. */
export interface BrowserStatement {
  id: string
  label: string
  articleNumbers: string[]
  /** The statement the browser was opened from. */
  current: boolean
}

type ColumnKey = "name" | "supplierCode"

interface Column {
  key: ColumnKey
  labelKey: TranslationKeys
  value: (a: CandidateArticle) => string
}

/** Hit lines under a row before the rest fold into "+n": an article with dozens
 *  of import columns would otherwise grow one row past the viewport. */
const HIT_LINES = 2

/** The columns the reviewer can show beside the article number. The search
 *  runs over every article field (`articleSearch`), not just these. */
const COLUMNS: Column[] = [
  {
    key: "name",
    labelKey: "MC_DECL_V2_BROWSER_COL_NAME",
    value: (a) => a.name,
  },
  {
    key: "supplierCode",
    labelKey: "MC_DECL_V2_BROWSER_COL_SUPPLIER_CODE",
    value: (a) => a.supplierCode ?? "",
  },
]

/** Rows rendered before the list asks the reviewer to narrow down further. */
const ROW_CAP = 400

/** Stable empty defaults: an inline `[]` default parameter is a fresh
 *  reference on every render, which would defeat the counts' `useMemo`. */
const NO_SUGGESTIONS: SearchSuggestion[] = []
const NO_STATEMENTS: BrowserStatement[] = []

export interface ArticleBrowserProps {
  pool: CandidateArticle[]
  /** Restrict browsing to these numbers (the remainder's uncovered set). */
  restrictTo?: string[] | null
  /** The applied set: what the statement holds right now. */
  applied: string[]
  suggestions?: SearchSuggestion[]
  /** A note above the suggestions (MC_ISSUER_SCOPE_PROPOSAL: the supplier
   *  carries several manufacturers and none matched the issuer). */
  suggestionsNote?: ReactNode
  statements?: BrowserStatement[]
  /** The document pane's find bar. While the reviewer types, the table shows
   *  the rows matching the live text OR any committed chip; Enter turns the
   *  text into a chip and the trailing OR is open again: every row shows
   *  until the next keystroke. Chips alone never narrow the table. */
  tokens?: FilterToken[]
  search?: string
  /** Clears the SHARED find expression (`tokens`/`search` above) through the
   *  document pane's own setters: the caller's, never a second, browser-
   *  local one (the expression is not this component's to own, see
   *  `tokens`/`search`'s own doc). Wired to the filter chip's × (below).
   *  Live-test finding L7, wave 5: the browser is filtered by an expression
   *  it never shows a box for, so a search left over from the document pane
   *  emptied the table with no visible cause ("Treffer 0" while the rail
   *  still counted the real pool). Omitted, the chip still names the term
   *  but renders no clear control, every existing caller/test predating
   *  this. */
  onClearFind?: () => void
  /** The header's search icon. The find bar lives in the document pane on
   *  the other side of the screen; this puts the cursor into it (and opens
   *  it again if the reviewer had closed it) without leaving the table. */
  onFindRequest?: () => void
  /** The statement sentence as it stands now, shown in the footer, so the
   *  reviewer always sees which statement (and what state) they are choosing
   *  for. */
  current: ReactNode
  initialView?: BrowserView
  onCancel: () => void
  /** The footer's commit control for the staged set: the caller owns the
   *  write (a statement's scope Apply, or the remainder's status pick). */
  renderCommit: (staged: string[], dirty: boolean) => ReactNode
  /** Required for the "zuordnen" remap popover (`useSearchArticles`,
   *  `useRemapArticle` both key off it). Omitted, a row never offers the
   *  popover: used by call sites that have no declaration to remap into. */
  declId?: string
  /** The statement's Geltung, as the reviewer currently reads it: only
   *  `material` renders the "turnus learns your mapping" footer hint, same
   *  condition as `reviewmask/ArticleSlot.tsx`'s own binary toggle. */
  geltung?: Geltung
}

/**
 * The article browser (review-mask prototype, round 3): how a reviewer
 * connects a statement to articles out of a 300-row pool without a 300-row
 * card. It takes the place of the statement cards under the Verordnung
 * header (`ReviewMaskPane`), so the reviewer stays oriented: the footer
 * states, beside Abbrechen and the commit control, the sentence of the
 * statement they are choosing articles for. Left: quick views, all
 * articles, the searches the statement's unresolved references suggest,
 * and the other statements' applied article sets. Right: the table, first
 * thing: its header row carries the master checkbox, the ID column, the
 * reviewer's chosen second column and the "Treffer | Gewählt" filter. It is
 * filtered over every article field by the document pane's find bar, with
 * every hit in a field that is not shown reported in a small line under the
 * row.
 */
export function ArticleBrowser({
  pool,
  restrictTo = null,
  applied,
  suggestions = NO_SUGGESTIONS,
  suggestionsNote,
  statements = NO_STATEMENTS,
  tokens = [],
  search = "",
  onClearFind,
  onFindRequest,
  current,
  initialView,
  onCancel,
  renderCommit,
  declId,
  geltung,
}: ArticleBrowserProps) {
  const { translate } = useTranslation()
  const [staged, setStaged] = useState<Set<string>>(() => new Set(applied))
  const [view, setView] = useState<BrowserView>(initialView ?? { kind: "all" })
  const [column, setColumn] = useState<ColumnKey>("name")
  const [onlySelected, setOnlySelected] = useState(false)
  // This session's own "gemerkt" memory, mirroring `ArticleSlot`'s own
  // `learned` set: see that file's doc for why it is not persisted.
  const [learned, setLearned] = useState<ReadonlySet<string>>(new Set())

  const base = useMemo(() => {
    if (!restrictTo) return pool
    const allowed = new Set(restrictTo)
    return pool.filter((a) => allowed.has(a.articleNumber))
  }, [pool, restrictTo])
  const dirty =
    staged.size !== applied.length || applied.some((n) => !staged.has(n))

  // Union semantics, matching `ArticleSlot`: a staged number outside
  // `restrictTo` still counts toward the map-once hint, so switching to a
  // quick view never hides an unmapped row already staged.
  const byNumber = useMemo(
    () => new Map(pool.map((a) => [a.articleNumber, a])),
    [pool],
  )
  const anyStagedUnmapped = useMemo(
    () => [...staged].some((n) => isUnmapped(byNumber.get(n))),
    [staged, byNumber],
  )
  const noneStaged = staged.size === 0 && base.length > 0

  const suggestion =
    view.kind === "suggestion" ? suggestions[view.index] : undefined
  const statement =
    view.kind === "statement"
      ? statements.find((s) => s.id === view.cardId)
      : undefined
  const live = search.trim().toLowerCase()
  const findTerms = useMemo(() => textTerms(tokens, search), [tokens, search])
  const terms = useMemo(
    () => [...findTerms, ...(suggestion?.terms ?? [])],
    [findTerms, suggestion],
  )

  /** Quick-view hit counts: one pass over `base` instead of a per-row
   *  re-filter, recomputed only when the pool, suggestions or statements
   *  actually change (see `browserCounts.buildCounts`). */
  const counts = useMemo(
    () =>
      browserCounts.buildCounts(
        base,
        suggestions,
        statements,
        referenceMatches,
      ),
    [base, suggestions, statements],
  )
  const suggestionCount = (_s: SearchSuggestion, i: number) =>
    counts.suggestions[i] ?? 0
  const statementCount = (s: BrowserStatement) =>
    counts.statements.get(s.id) ?? 0

  /** Rows the view and the find bar leave, before the Gewählt filter. */
  const matched = useMemo(() => {
    let out = base
    if (statement) {
      const set = new Set(statement.articleNumbers)
      out = out.filter((a) => set.has(a.articleNumber))
    }
    if (suggestion?.articleNumbers) {
      const set = new Set(suggestion.articleNumbers)
      out = out.filter((a) => set.has(a.articleNumber))
    } else if (suggestion) {
      const lower = suggestion.terms.map((t) => t.toLowerCase())
      out = out.filter((a) => referenceMatches(a, lower))
    }
    if (live) {
      const chips = tokens.map((t) => t.term.q.toLowerCase())
      out = out.filter((a) => articleMatches(a, [live, ...chips]))
    }
    return out
  }, [base, statement, suggestion, tokens, live])
  const matchedSelected = matched.filter((a) =>
    staged.has(a.articleNumber),
  ).length
  const rows = onlySelected
    ? matched.filter((a) => staged.has(a.articleNumber))
    : matched

  /** The fixed set the open suggestion names, as far as the browsed pool holds
   *  it. Not the view's `rows`: the find bar and the Gewählt filter narrow
   *  those, and neither says which articles the suggestion is about. */
  const fixedSet = useMemo(() => {
    if (!suggestion?.articleNumbers) return []
    const set = new Set(suggestion.articleNumbers)
    return base.map((a) => a.articleNumber).filter((n) => set.has(n))
  }, [base, suggestion])
  // A whole-pool statement opens with every article ticked: the suggestion's
  // rows all read as chosen while the commit still says the whole pool, and
  // the master checkbox could only untick them.
  const offerFixedSet =
    fixedSet.length > 0 &&
    (staged.size !== fixedSet.length || fixedSet.some((n) => !staged.has(n)))

  const col = COLUMNS.find((c) => c.key === column) ?? COLUMNS[0]
  const lowerTerms = terms.map((t) => t.toLowerCase())
  /** Fields other than the two shown that carry a hit, reported under the
   *  row, as a snippet around the first match when the value is long: a
   *  master-data field by its label, an import column by its own header. */
  const otherHits = (
    a: CandidateArticle,
  ): { key: string; label: string; text: string }[] => {
    if (lowerTerms.length === 0) return []
    const out: { key: string; label: string; text: string }[] = []
    for (const f of articleFields(a)) {
      if (f.key === "articleNumber" || f.key === col.key) continue
      const idx = Math.min(
        ...lowerTerms.map((t) => f.lower.indexOf(t)).filter((i) => i >= 0),
      )
      if (!Number.isFinite(idx)) continue
      // The index is into the lowercased text: slice the original only when
      // lowercasing kept its length ("İ" grows to two code units).
      const sliceable = idx > 20 && f.lower.length === f.value.length
      out.push({
        key: f.key,
        label: f.labelKey ? translate(f.labelKey) : (f.label ?? f.key),
        text: sliceable ? `…${f.value.slice(idx - 12)}` : f.value,
      })
    }
    return out
  }
  /** A hit in the shown column lets its value wrap, so the match is never
   *  lost behind the ellipsis. */
  const shownHit = (a: CandidateArticle) => {
    const shown = articleFields(a).find((f) => f.key === col.key)
    return !!shown && lowerTerms.some((t) => fieldHas(shown, t))
  }

  const toggle = (n: string) =>
    setStaged((prev) => {
      const next = new Set(prev)
      if (next.has(n)) next.delete(n)
      else next.add(n)
      return next
    })
  const allRowsSelected =
    rows.length > 0 && rows.every((a) => staged.has(a.articleNumber))
  const someRowsSelected = rows.some((a) => staged.has(a.articleNumber))
  const selectRows = () =>
    setStaged((prev) => {
      const next = new Set(prev)
      if (allRowsSelected) rows.forEach((a) => next.delete(a.articleNumber))
      else rows.forEach((a) => next.add(a.articleNumber))
      return next
    })
  const masterLabel = allRowsSelected
    ? translate("MC_DECL_V2_BROWSER_DESELECT_ALL", { n: rows.length })
    : translate("MC_DECL_V2_BROWSER_SELECT_ALL", { n: rows.length })

  const viewRow = (
    key: string,
    on: boolean,
    label: ReactNode,
    n: number,
    onClick: () => void,
    extra?: string,
    title?: string,
  ) => (
    <button
      key={key}
      type="button"
      className={cn("sv2-bviewrow", on && "sv2-on", extra)}
      aria-pressed={on}
      onClick={onClick}
      title={title}
    >
      <span className="truncate">{label}</span>
      <span className="sv2-n">{n}</span>
    </button>
  )

  return (
    <div
      className="sv2-browser"
      data-testid="article-browser"
      onKeyDown={(e) => {
        // Escape closes THIS browser, never the whole workspace
        // (`useStudioV2Keyboard`'s own Esc leaves for the list).
        if (e.key !== "Escape") return
        e.stopPropagation()
        onCancel()
      }}
    >
      <div className="sv2-bgrid">
        <div className="sv2-bviews">
          {viewRow(
            "all",
            view.kind === "all",
            translate("MC_DECL_V2_BROWSER_VIEW_ALL"),
            base.length,
            () => setView({ kind: "all" }),
            "sv2-bviewall",
          )}

          <div className="sv2-bviewgrp">
            <div className="sv2-bviewlbl">
              {translate("MC_DECL_V2_BROWSER_SUGGESTIONS")}
            </div>
            {suggestionsNote}
            {suggestions.length === 0 && (
              <p className="sv2-bviewempty">
                {translate("MC_DECL_V2_BROWSER_SUGGESTIONS_EMPTY")}
              </p>
            )}
            {suggestions.map((s, i) =>
              viewRow(
                `sg-${i}`,
                view.kind === "suggestion" && view.index === i,
                s.label,
                suggestionCount(s, i),
                () => setView({ kind: "suggestion", index: i }),
                undefined,
                // The query itself, and (for a resolver-chain suggestion)
                // the document span M2 cited for it.
                [
                  translate("MC_DECL_V2_BROWSER_SUGGESTION_TITLE", {
                    terms: s.terms.join(" · "),
                  }),
                  ...(s.cite
                    ? [
                        translate("MC_DECL_V2_ARTS_PER_DOCUMENT", {
                          quote: s.cite,
                        }),
                      ]
                    : []),
                ].join("\n"),
              ),
            )}
          </div>

          <div className="sv2-bviewgrp">
            <div className="sv2-bviewlbl">
              {translate("MC_DECL_V2_BROWSER_STATEMENTS")}
            </div>
            {statements.map((s) =>
              viewRow(
                s.id,
                view.kind === "statement" && view.cardId === s.id,
                <>
                  {s.current && <span className="sv2-curdot" />}
                  {s.label}
                </>,
                statementCount(s),
                () => setView({ kind: "statement", cardId: s.id }),
                undefined,
                translate(
                  s.current
                    ? "MC_DECL_V2_BROWSER_STATEMENT_CURRENT_TITLE"
                    : "MC_DECL_V2_BROWSER_STATEMENT_TITLE",
                  { label: s.label },
                ),
              ),
            )}
          </div>
        </div>

        <div className="sv2-blist">
          <div className="sv2-blhead">
            <button
              type="button"
              role="checkbox"
              aria-checked={
                allRowsSelected ? "true" : someRowsSelected ? "mixed" : "false"
              }
              aria-label={masterLabel}
              title={masterLabel}
              className={cn(
                "sv2-ck sv2-bmaster",
                allRowsSelected && "sv2-on",
                someRowsSelected && !allRowsSelected && "sv2-mixed",
              )}
              disabled={rows.length === 0}
              onClick={selectRows}
            >
              {allRowsSelected ? (
                <Check size={10} />
              ) : someRowsSelected ? (
                <Minus size={10} />
              ) : null}
            </button>
            <span>{translate("MC_DECL_V2_BROWSER_COL_ID")}</span>
            <Select
              value={column}
              onValueChange={(v) => setColumn(v as ColumnKey)}
            >
              <SelectPrimitive.Trigger
                className="sv2-colpick"
                aria-label={translate("MC_DECL_V2_BROWSER_COL_PICK_ARIA")}
              >
                {translate(col.labelKey)}
                <span className="sv2-car">▾</span>
              </SelectPrimitive.Trigger>
              <SelectContent>
                {COLUMNS.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {translate(c.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div
              className="sv2-bseg"
              role="group"
              aria-label={translate("MC_DECL_V2_BROWSER_FILTER_ARIA")}
            >
              <button
                type="button"
                className={cn(!onlySelected && "sv2-on")}
                aria-pressed={!onlySelected}
                onClick={() => setOnlySelected(false)}
              >
                {translate("MC_DECL_V2_BROWSER_HITS")}{" "}
                <span className="sv2-n">{matched.length}</span>
              </button>
              <button
                type="button"
                className={cn(onlySelected && "sv2-on")}
                aria-pressed={onlySelected}
                onClick={() => setOnlySelected(true)}
                title={translate("MC_DECL_V2_BROWSER_SELECTED_TITLE")}
              >
                {translate("MC_DECL_V2_BROWSER_SELECTED")}{" "}
                <span className="sv2-n">{matchedSelected}</span>
              </button>
            </div>
            {onFindRequest && (
              <button
                type="button"
                className="sv2-bfind"
                aria-label={translate("MC_DECL_V2_BROWSER_FIND")}
                title={translate("MC_DECL_V2_BROWSER_FIND")}
                onClick={onFindRequest}
              >
                <Search size={12} aria-hidden="true" />
              </button>
            )}
          </div>
          {/* Live-test finding L7 (wave 5): `findTerms` is the document
              pane's own find bar (chips + live input, see `tokens`/`search`'s
              own doc), NOT `terms` below, which also folds in the current
              quick view's suggestion terms, already visible as that view's
              own pressed state in the sidebar. The shared expression is the
              one filter this table applies with no on-screen box of its own,
              so it is the one that gets a chip: a "Treffer 0" the reviewer
              never asked for here must always show why. */}
          {findTerms.length > 0 && (
            <div className="sv2-bfilterchip" data-testid="find-filter-chip">
              <span>
                {translate("MC_DECL_V2_BROWSER_FIND_FILTER", {
                  terms: findTerms.join(", "),
                })}
              </span>
              <button
                type="button"
                aria-label={translate("MC_DECL_V2_BROWSER_FIND_CLEAR")}
                onClick={() => onClearFind?.()}
              >
                <X size={10} />
              </button>
            </div>
          )}
          {offerFixedSet && (
            <div className="sv2-bonly">
              <button
                type="button"
                className="sv2-hact sv2-hact-primary"
                onClick={() => setStaged(new Set(fixedSet))}
              >
                {translate("MC_DECL_V2_ISSUER_SELECT_ONLY", {
                  n: fixedSet.length,
                })}
              </button>
            </div>
          )}
          {rows.length === 0 && (
            <p className="sv2-acempty">
              {translate("MC_DECL_V2_BROWSER_EMPTY")}
            </p>
          )}
          {rows.slice(0, ROW_CAP).map((a) => {
            const n = a.articleNumber
            const on = staged.has(n)
            const value = col.value(a)
            const unresolved = isUnmapped(a)
            const hits = otherHits(a)
            const moreHits = hits.slice(HIT_LINES)
            return (
              // A `<div>`, not the `<label>` itself: the "zuordnen" popover
              // sits as a SIBLING of the label so its clicks never delegate
              // into the row checkbox (a Map button inside the label would
              // toggle the row on every click).
              <div key={n} className={cn("sv2-brow", on && "sv2-on")}>
                <label>
                  <input
                    type="checkbox"
                    className="sr-only"
                    aria-label={n}
                    checked={on}
                    onChange={() => toggle(n)}
                  />
                  <span className="sv2-ck">{on && <Check size={10} />}</span>
                  <span className="sv2-bno">
                    <Highlight
                      text={n}
                      terms={terms}
                      className="sv2-artmatch"
                    />
                  </span>
                  <span className="sv2-bcell">
                    <span
                      className={cn("sv2-bval", shownHit(a) && "sv2-bwrap")}
                      title={value}
                    >
                      {value ? (
                        <Highlight
                          text={value}
                          terms={terms}
                          className="sv2-artmatch"
                        />
                      ) : (
                        <span className="sv2-bempty">–</span>
                      )}
                    </span>
                    {hits.slice(0, HIT_LINES).map((h) => (
                      <span
                        key={h.key}
                        className="sv2-bhit"
                        title={translate("MC_DECL_V2_BROWSER_HIT_IN", {
                          column: h.label,
                        })}
                      >
                        <span className="sv2-bhitcol">{h.label}</span>
                        <Highlight
                          text={h.text}
                          terms={terms}
                          className="sv2-artmatch"
                        />
                      </span>
                    ))}
                    {moreHits.length > 0 && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          {/* Focusable so a keyboard reaches the folded names;
                              preventDefault keeps a click off the row checkbox
                              the surrounding label would otherwise toggle. */}
                          <TooltipTrigger asChild>
                            <span
                              className="sv2-bhit"
                              tabIndex={0}
                              aria-label={translate(
                                "MC_DECL_V2_BROWSER_HIT_MORE_TITLE",
                                {
                                  columns: moreHits
                                    .map((h) => h.label)
                                    .join(", "),
                                },
                              )}
                              onClick={(e) => e.preventDefault()}
                            >
                              <span className="sv2-bhitcol">
                                {translate("MC_DECL_V2_BROWSER_HIT_MORE", {
                                  count: moreHits.length,
                                })}
                              </span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-[280px] border border-border"
                          >
                            {translate("MC_DECL_V2_BROWSER_HIT_MORE_TITLE", {
                              columns: moreHits.map((h) => h.label).join(", "),
                            })}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                </label>
                {unresolved && declId && (
                  <RemapPopover
                    declId={declId}
                    article={a}
                    onLearned={() => setLearned((prev) => new Set(prev).add(n))}
                  />
                )}
                {learned.has(n) && (
                  <span className="sv2-learned">
                    {translate("MC_DECL_V2_ARTS_MAPPED_CHIP")}
                  </span>
                )}
              </div>
            )
          })}
          {rows.length > ROW_CAP && (
            <p className="sv2-hint px-2 py-2">
              {translate("MC_DECL_V2_BROWSER_MORE", {
                n: rows.length - ROW_CAP,
              })}
            </p>
          )}
        </div>
      </div>

      <div className="sv2-bfoot">
        <span className="sv2-bcurrent">
          <span className="sv2-bcurk">
            {translate("MC_DECL_V2_BROWSER_CURRENT")}
          </span>
          {current}
        </span>
        {noneStaged && (
          <span className="sv2-bhint">
            {translate("MC_DECL_V2_ARTS_NONE_SELECTED")}
          </span>
        )}
        {!noneStaged && anyStagedUnmapped && (
          <span className="sv2-bhint sv2-artnote">
            <Lightbulb size={12} className="sv2-ic" />
            {translate("MC_DECL_V2_ARTS_MAP_HINT")}
          </span>
        )}
        {geltung === "material" && (
          <span className="sv2-bhint sv2-mathint">
            {translate("MC_DECL_V2_ARTS_MATERIAL_HINT")}
          </span>
        )}
        <button
          type="button"
          className="sv2-hact sv2-hact-ghost"
          onClick={onCancel}
        >
          {translate("MC_DECL_V2_BROWSER_CANCEL")}
        </button>
        {renderCommit([...staged], dirty)}
      </div>
    </div>
  )
}
